// Static content + asset URLs for the SisterSafe landing page
import {
  Route,
  HeartHandshake,
  Siren,
  Users,
  MapPin,
  ShieldCheck,
  Eye,
  BadgeCheck,
  Fingerprint,
} from "lucide-react";

export const CONTACT_EMAIL = "hello@sistersafe.app";
export const APP_STORE_URL = "https://apps.apple.com/ca/app/sistersafe/id6786626474";

export const ASSETS = {
  logoIcon: "/sistersafe-icon.png",
  logoFull: "/sistersafe-logo.png",
  appHome: "/screens/home.webp",
  safeJourney: "/screens/safe-journey.webp",
  sosActive: "/screens/sos.webp",
  safePlaces: "/screens/safe-places.webp",
  communityWatch: "/screens/community-watch.webp",
  communityLifestyle: "/screens/community-lifestyle.webp",
};

export const FEATURES = [
  {
    icon: Route,
    title: "Safe Journey",
    desc: "Set a destination and let your trusted circle follow along until you've arrived exactly where you meant to be.",
  },
  {
    icon: HeartHandshake,
    title: "Date Safety",
    desc: "Share the details of a date with someone you trust, with gentle check-ins so you can focus on the moment.",
  },
  {
    icon: Siren,
    title: "SOS Alerts",
    desc: "One tap quietly notifies your circle with your live location, so help is always a moment away.",
  },
  {
    icon: Users,
    title: "Trusted Circle",
    desc: "Invite the people who matter most and decide exactly who can look out for you, and when.",
  },
  {
    icon: MapPin,
    title: "Live Location Sharing",
    desc: "Share your real time location on your terms, for a journey, an evening, or whenever you choose.",
  },
  {
    icon: ShieldCheck,
    title: "Safe Places",
    desc: "Discover verified safe spaces nearby, like cafés, pharmacies, and partners ready to help.",
  },
  {
    icon: Eye,
    title: "Community Watch",
    desc: "A supportive community sharing helpful, local insights so everyone feels more confident getting around.",
  },
  {
    icon: BadgeCheck,
    title: "Endorse",
    desc: "Vouch for the women you trust and build a network rooted in genuine, real-world connection.",
  },
  {
    icon: Fingerprint,
    title: "ID Verification",
    desc: "Identity verification helps build a trusted community where women can confidently connect with one another.",
  },
];

export const STEPS = [
  {
    num: "01",
    title: "Create your account",
    desc: "Sign up in seconds and set up a profile that's truly yours.",
  },
  {
    num: "02",
    title: "Verify your identity",
    desc: "Identity verification helps keep every member of the SisterSafe community authentic, trusted, and safe.",
  },
  {
    num: "03",
    title: "Build your trusted circle",
    desc: "Invite the friends and family you trust most to look out for you.",
  },
  {
    num: "04",
    title: "Stay connected",
    desc: "Travel, commute, date, and explore. Wherever life takes you, your people are with you.",
  },
];

export const PRIVACY = [
  {
    title: "Only what's necessary",
    desc: "We collect only the information necessary to provide SisterSafe's core features.",
  },
  {
    title: "Strong security",
    desc: "We use industry-standard security measures to help protect your personal information and live location.",
  },
  {
    title: "You control your location",
    desc: "You choose when to share your location and who you share it with. You can stop location sharing at any time.",
  },
  {
    title: "Verification builds trust",
    desc: "We verify every member to help create a trusted community for women.",
  },
];

export const PRIVACY_UPDATED = "August 2026";

export const PRIVACY_DETAILS = [
  {
    heading: "Information We Collect",
    paragraphs: [
      "SisterSafe collects information necessary to provide and protect our safety features. This may include information you provide when creating or managing your account, such as your name, email address, profile information, and other information you choose to provide.",
      "Depending on the features you use, SisterSafe may also process location information, safety activity, trusted-circle connections, community safety reports, and information provided during identity verification.",
      "We aim to collect only the information reasonably necessary to operate SisterSafe and provide its safety features.",
    ],
  },
  {
    heading: "How We Use Your Information",
    paragraphs: [
      "We use your information to create and manage your account, provide SisterSafe's safety and location-sharing features, verify users, facilitate trusted connections, deliver safety alerts and notifications, maintain the security and integrity of the service, respond to support requests, and improve SisterSafe.",
      "We do not sell your personal information.",
    ],
  },
  {
    heading: "Location Data",
    paragraphs: [
      "Location sharing is an important part of several SisterSafe safety features.",
      "You control when you share your location and which permitted SisterSafe users can view it through applicable features. Depending on the feature you choose to use, location information may be used to support features such as Safe Journey, Date Safety, emergency or SOS functions, nearby safety features, and other location-based services.",
      "You can stop location sharing when the applicable feature allows you to end the sharing session. SisterSafe processes location information only as necessary to provide the location-based features you choose to use.",
    ],
  },
  {
    heading: "Identity Verification",
    paragraphs: [
      "SisterSafe uses identity verification to help maintain a trusted community.",
      "During verification, users may be asked to provide information such as an identification document and verification selfie. This information is used for identity and eligibility verification purposes.",
      "Verification documents, including identification images and verification selfies, are automatically deleted in accordance with SisterSafe's verification-data retention process.",
      "SisterSafe does not use verification documents for advertising purposes.",
    ],
  },
  {
    heading: "How We Share Information",
    paragraphs: [
      "SisterSafe does not sell your personal information.",
      "Information may be shared with other SisterSafe users when you intentionally use features designed for sharing, such as trusted-circle connections, location sharing, safety alerts, or community safety features.",
      "We may also use service providers that process information on our behalf where necessary to operate, secure, maintain, and support SisterSafe. Information may also be disclosed where required by law or where reasonably necessary to protect the safety, rights, or security of SisterSafe, our users, or others.",
    ],
  },
  {
    heading: "Data Retention and Deletion",
    paragraphs: [
      "SisterSafe retains personal information only for as long as reasonably necessary to provide the service, maintain safety and security, comply with applicable legal obligations, and fulfil the purposes described in this privacy information.",
      "Verification documents, including identification images and verification selfies, are automatically deleted in accordance with SisterSafe's verification-data retention process.",
      "Users can delete their SisterSafe account directly within the app through their account settings. When an account is deleted, the account and associated personal information are deleted, except for information that SisterSafe may need to retain where required or permitted for legal, security, fraud-prevention, dispute-resolution, or regulatory purposes.",
      "Users may also contact SisterSafe regarding deletion of specific personal information without necessarily deleting their entire account.",
    ],
  },
  {
    heading: "Your Rights and Choices",
    paragraphs: [
      "You may request access to, correction of, or deletion of your personal information by contacting SisterSafe at hello@sistersafe.app.",
      "You may also request deletion of specific personal information without deleting your entire SisterSafe account by contacting hello@sistersafe.app.",
      "To delete your entire account, you can use the account deletion option available within the SisterSafe app. You may also contact us if you require assistance with an account or data deletion request.",
      "We may need to verify your identity before processing certain privacy requests in order to protect your account and personal information.",
    ],
  },
  {
    heading: "Security",
    paragraphs: [
      "SisterSafe uses reasonable technical and organizational safeguards designed to protect personal information, including sensitive information such as location data.",
      "We continuously aim to maintain safeguards appropriate to the nature of the information processed by SisterSafe.",
    ],
  },
  {
    heading: "Teen Users",
    paragraphs: [
      "SisterSafe is intended for users aged 16 and older.",
      "Because privacy and safety are particularly important for younger users, SisterSafe aims to apply appropriate protections to accounts and safety features available to users aged 16 and 17.",
      "Users should only share location or safety information with people they know and trust.",
    ],
  },
  {
    heading: "Changes to This Privacy Information",
    paragraphs: [
      "We may update this privacy information as SisterSafe develops, new features are introduced, or our privacy practices or legal obligations change.",
      "When material changes are made, the updated information will be published on this page with a revised \u201cLast updated\u201d date.",
    ],
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "SisterSafe has completely changed how I feel about my late commute. Knowing my sister can see I made it home — without me having to text every time — is everything.",
    name: "Amara O.",
    role: "Beta Tester · London",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
  {
    quote:
      "I used the Date Safety feature on a first date and it felt so natural. My best friend knew where I was, and I felt confident and present the whole evening.",
    name: "Priya N.",
    role: "Beta Tester · Toronto",
    avatar:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
  {
    quote:
      "It's not about fear — it's about feeling capable. SisterSafe gave me a community of women who genuinely look out for one another. I feel empowered.",
    name: "Sofia M.",
    role: "Beta Tester · Berlin",
    avatar:
      "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
];

export const FAQS = [
  {
    q: "Is SisterSafe free?",
    a: "SisterSafe will offer a free plan with the core safety features that matter most, plus an optional premium tier for advanced tools.",
  },
  {
    q: "When will it launch?",
    a: "We're in active beta now and refining the experience with real users. Join the waitlist to be among the first to know our public launch date and to get early access.",
  },
  {
    q: "Is my location always shared?",
    a: "Never. Location sharing is always on your terms. You decide who sees it and exactly when, and you can start or stop sharing at any moment, for any reason.",
  },
  {
    q: "Can I use it while travelling?",
    a: "Absolutely. SisterSafe is built for commuting, travelling, dating, shopping, and simply getting home. Wherever life takes you, your trusted circle can be there with you.",
  },
  {
    q: "Is it only for women?",
    a: "SisterSafe is designed with women in mind and built around their everyday safety. Anyone who shares those values and wants to support a trusted circle is welcome in the community.",
  },
];
